import { useQuery } from '@tanstack/react-query'

import { checkoutService } from '@leanpay/services'

import { CheckoutQueryKey } from './cache-keys'

/**
 * Address codebook lookups (settlement / street / post-zip).
 *
 * All of them are read-only reference data driven purely by their argument, so
 * they carry no app state and are shared verbatim by checkout, pre-qualify and
 * self-checkout.
 *
 * Each hook keys on its own lookup axis and includes every argument the queryFn
 * reads — see the note in `cache-keys.ts` for why sharing a `Settlement` /
 * `Streets` namespace across id spaces silently served the wrong list.
 */

export const useGetSettlementsByStreet = (streetId: string) => {
    return useQuery({
        queryKey: [CheckoutQueryKey.SettlementByStreet, streetId],
        enabled: !!streetId,
        refetchOnWindowFocus: true,
        queryFn: () => checkoutService.getSettlementsByStreet(streetId),
    })
}

export const useGetSettlementsByPostZip = (postZipCode: string) => {
    return useQuery({
        queryKey: [CheckoutQueryKey.SettlementByPostZip, postZipCode],
        enabled: !!postZipCode,
        queryFn: () => checkoutService.getSettlementsByPostZip(postZipCode),
    })
}

export const useGetStreetsBySettlement = (settlementId: string) => {
    return useQuery({
        queryKey: [CheckoutQueryKey.StreetsBySettlement, settlementId],
        enabled: !!settlementId,
        refetchOnWindowFocus: true,
        queryFn: () => checkoutService.getStreetsBySettlement(settlementId),
    })
}

export const useGetStreetsByPostZip = (zipCode: string) => {
    return useQuery({
        queryKey: [CheckoutQueryKey.StreetsByPostZip, zipCode],
        enabled: !!zipCode,
        queryFn: () => checkoutService.getStreetsByPostZip(zipCode),
    })
}

export const useGetPostZipsByCountry = (countryCodebookId: string) => {
    return useQuery({
        queryKey: [CheckoutQueryKey.PostZip, countryCodebookId],
        enabled: !!countryCodebookId,
        queryFn: () => checkoutService.getPostZips(countryCodebookId),
    })
}

export const useGetPostZipsBySettlement = (settlement: string) => {
    return useQuery({
        queryKey: [CheckoutQueryKey.PostZipBySettlement, settlement],
        enabled: !!settlement,
        queryFn: () => checkoutService.getPostZipsBySettlement(settlement),
    })
}
